import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowRightLeft, Package, Plus, Trash2, Calendar, FileText,
  Building2, Users, Truck, ShoppingCart, Loader2, X, RotateCcw,
  Search, Check, ChevronsUpDown, Download, Filter, Trash, Edit2, Clock,
  PackagePlus
} from 'lucide-react';

// Searchable Product Selector Component
const ProductSelector = ({ products, value, onChange, placeholder = "Search product..." }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Get selected product details
  const selectedProduct = products.find(p => p.id === value);

  // Filter products based on search (matches SKU or name)
  const filteredProducts = products.filter(product => {
    const searchLower = search.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.sku.toLowerCase().includes(searchLower)
    );
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (productId) => {
    onChange(productId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-left text-sm bg-white hover:bg-slate-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 flex items-center justify-between gap-2"
      >
        <span className={`truncate ${!selectedProduct ? 'text-slate-400' : ''}`}>
          {selectedProduct ? `${selectedProduct.sku} - ${selectedProduct.name}` : placeholder}
        </span>
        <ChevronsUpDown size={16} className="text-slate-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type SKU or product name..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 focus:outline-none"
                autoFocus
              />
            </div>
          </div>

          {/* Product List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredProducts.length === 0 ? (
              <div className="py-4 px-3 text-sm text-slate-500 text-center">
                No products found
              </div>
            ) : (
              filteredProducts.slice(0, 50).map(product => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => handleSelect(product.id)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-cyan-50 flex items-center gap-2 ${
                    value === product.id ? 'bg-cyan-50 text-cyan-700' : ''
                  }`}
                >
                  {value === product.id && <Check size={14} className="text-cyan-600 flex-shrink-0" />}
                  <span className={`truncate ${value === product.id ? '' : 'ml-5'}`}>
                    <span className="font-medium text-slate-600">{product.sku}</span>
                    <span className="text-slate-400 mx-1">-</span>
                    <span>{product.name}</span>
                  </span>
                </button>
              ))
            )}
            {filteredProducts.length > 50 && (
              <div className="py-2 px-3 text-xs text-slate-400 text-center border-t">
                Showing 50 of {filteredProducts.length} results. Type more to narrow down.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StockInwardOutward = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null); // Track which transaction is being deleted
  const [offices, setOffices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // Date filter state
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Form state
  const [transactionType, setTransactionType] = useState('purchase');
  const [isComboTransaction, setIsComboTransaction] = useState(false);
  const [fromType, setFromType] = useState('vendor');
  const [fromId, setFromId] = useState('');
  const [toType, setToType] = useState('warehouse');
  const [toId, setToId] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([{ product_id: '', quantity: 1 }]);
  const [comboItems, setComboItems] = useState([{ combo_id: '', quantity: 1 }]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Update from/to types based on transaction type
    if (transactionType === 'purchase') {
      setFromType('vendor');
      setToType('warehouse');
    } else if (transactionType === 'sales') {
      setFromType('warehouse');
      setToType('customer');
    } else if (transactionType === 'transfer') {
      setFromType('warehouse');
      setToType('warehouse');
    } else if (transactionType === 'return') {
      setFromType('customer');
      setToType('warehouse');
    } else if (transactionType === 'scrap') {
      setFromType('warehouse');
      setToType('scrap'); // Special type for scrap
    } else if (transactionType === 'existing_stock') {
      setFromType('initial');
      setToType('warehouse');
    }
    setFromId('');
    setToId('');
  }, [transactionType]);

  const fetchData = async () => {
    try {
      const [officesRes, vendorsRes, customersRes, productsRes, combosRes, transactionsRes] = await Promise.all([
        api.getStockOffices(),
        api.getStockVendors(),
        api.getStockCustomers(),
        api.getStockProducts(true),
        api.getStockCombos(true),
        api.getStockTransactions(null, 200) // Fetch more for filtering
      ]);
      setOffices(officesRes.data);
      setVendors(vendorsRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      setCombos(combosRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Filter transactions by date
  const filteredTransactions = transactions.filter(txn => {
    if (!filterStartDate && !filterEndDate) return true;
    const txnDate = new Date(txn.transaction_date).toISOString().split('T')[0];
    if (filterStartDate && txnDate < filterStartDate) return false;
    if (filterEndDate && txnDate > filterEndDate) return false;
    return true;
  });

  const getFromOptions = () => {
    if (fromType === 'vendor') return vendors;
    if (fromType === 'warehouse') return offices;
    if (fromType === 'customer') return customers;
    return [];
  };

  const getToOptions = () => {
    if (toType === 'customer') return customers;
    if (toType === 'warehouse') return offices;
    return [];
  };

  const addComboItem = () => {
    setComboItems([...comboItems, { combo_id: '', quantity: 1 }]);
  };

  const removeComboItem = (index) => {
    if (comboItems.length > 1) {
      setComboItems(comboItems.filter((_, i) => i !== index));
    }
  };

  const updateComboItem = (index, field, value) => {
    const newItems = [...comboItems];
    newItems[index][field] = value;
    setComboItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: 1 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'quantity' ? parseInt(value) || 0 : value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For scrap, only fromId is required; for existing_stock, only toId is required
    if (transactionType === 'scrap') {
      if (!fromId) {
        toast.error('Please select the warehouse to scrap from');
        return;
      }
    } else if (transactionType === 'existing_stock') {
      if (!toId) {
        toast.error('Please select the warehouse for existing stock entry');
        return;
      }
    } else {
      if (!fromId || !toId) {
        toast.error('Please select From and To locations');
        return;
      }
    }

    // Validate items based on transaction mode
    if (isComboTransaction && transactionType !== 'existing_stock') {
      const validCombos = comboItems.filter(item => item.combo_id && item.quantity > 0);
      if (validCombos.length === 0) {
        toast.error('Please add at least one combo');
        return;
      }
    } else {
      const validItems = items.filter(item => item.product_id && item.quantity > 0);
      if (validItems.length === 0) {
        toast.error('Please add at least one product');
        return;
      }
    }

    setSubmitting(true);
    try {
      // Existing stock entry is product-only, not combo
      const useExistingStock = transactionType === 'existing_stock';
      const effectiveFromType = useExistingStock ? 'initial' : fromType;
      const effectiveFromId = useExistingStock ? 'initial' : fromId;
      
      if (isComboTransaction && !useExistingStock) {
        // Combo transaction
        const validCombos = comboItems.filter(item => item.combo_id && item.quantity > 0);
        const data = {
          transaction_type: transactionType,
          from_type: fromType,
          from_id: fromId,
          to_type: transactionType === 'scrap' ? 'scrap' : toType,
          to_id: transactionType === 'scrap' ? 'scrap' : toId,
          combos: validCombos,
          reference_number: referenceNumber || null,
          transaction_date: new Date(transactionDate).toISOString(),
          remarks: remarks || null
        };
        await api.createComboTransaction(data);
        toast.success('Combo transaction created - stock updated for all items in combos');
        setComboItems([{ combo_id: '', quantity: 1 }]);
      } else {
        // Regular transaction (also handles existing_stock)
        const validItems = items.filter(item => item.product_id && item.quantity > 0);
        const data = {
          transaction_type: transactionType,
          from_type: effectiveFromType,
          from_id: effectiveFromId,
          to_type: transactionType === 'scrap' ? 'scrap' : toType,
          to_id: transactionType === 'scrap' ? 'scrap' : toId,
          items: validItems,
          reference_number: referenceNumber || null,
          transaction_date: new Date(transactionDate).toISOString(),
          remarks: remarks || null
        };
        await api.createStockTransaction(data);
        toast.success(useExistingStock ? 'Existing stock entry added successfully' : 'Transaction created successfully');
        setItems([{ product_id: '', quantity: 1 }]);
      }
      
      // Reset common form fields
      setReferenceNumber('');
      setRemarks('');
      setFromId('');
      setToId('');
      
      // Refresh transactions
      const transactionsRes = await api.getStockTransactions(null, 50);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This will reverse all inventory changes.')) {
      return;
    }

    setDeleting(transactionId);
    try {
      await api.deleteStockTransaction(transactionId);
      toast.success('Transaction deleted and inventory reversed');
      
      // Refresh transactions
      const transactionsRes = await api.getStockTransactions(null, 200);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    } finally {
      setDeleting(null);
    }
  };

  // Check if transaction is editable (within 24 hours)
  const isTransactionEditable = (txn) => {
    const createdAt = new Date(txn.created_at);
    const now = new Date();
    const hoursDiff = (now - createdAt) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  };

  // Get remaining time for edit
  const getRemainingEditTime = (txn) => {
    const createdAt = new Date(txn.created_at);
    const now = new Date();
    const hoursDiff = (now - createdAt) / (1000 * 60 * 60);
    const remainingHours = Math.max(0, 24 - hoursDiff);
    
    if (remainingHours <= 0) return null;
    if (remainingHours < 1) return `${Math.round(remainingHours * 60)} min left`;
    return `${Math.round(remainingHours)}h left`;
  };

  // Open edit modal
  const handleEditTransaction = (txn) => {
    if (!isTransactionEditable(txn)) {
      toast.error('This transaction can no longer be edited (24 hour limit exceeded)');
      return;
    }
    setEditingTransaction({
      ...txn,
      editItems: txn.items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.price || 0
      })),
      editTransactionDate: txn.transaction_date.split('T')[0],
      editReferenceNumber: txn.reference_number || '',
      editRemarks: txn.remarks || '',
      editFromId: txn.from_id,
      editToId: txn.to_id
    });
    setShowEditModal(true);
  };

  // Update edit form fields
  const updateEditField = (field, value) => {
    setEditingTransaction(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add item to edit form
  const addEditItem = () => {
    setEditingTransaction(prev => ({
      ...prev,
      editItems: [...prev.editItems, { product_id: '', quantity: 1, price: 0 }]
    }));
  };

  // Remove item from edit form
  const removeEditItem = (index) => {
    setEditingTransaction(prev => ({
      ...prev,
      editItems: prev.editItems.filter((_, i) => i !== index)
    }));
  };

  // Update edit item
  const updateEditItem = (index, field, value) => {
    setEditingTransaction(prev => ({
      ...prev,
      editItems: prev.editItems.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Submit edit
  const handleSubmitEdit = async () => {
    if (!editingTransaction) return;

    const validItems = editingTransaction.editItems.filter(item => item.product_id && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one product');
      return;
    }

    setEditSubmitting(true);
    try {
      const data = {
        transaction_type: editingTransaction.transaction_type,
        from_type: editingTransaction.from_type,
        from_id: editingTransaction.editFromId,
        to_type: editingTransaction.to_type,
        to_id: editingTransaction.editToId,
        items: validItems,
        reference_number: editingTransaction.editReferenceNumber || null,
        transaction_date: new Date(editingTransaction.editTransactionDate).toISOString(),
        remarks: editingTransaction.editRemarks || null
      };

      await api.updateStockTransaction(editingTransaction.id, data);
      toast.success('Transaction updated successfully');
      setShowEditModal(false);
      setEditingTransaction(null);
      
      // Refresh transactions
      const transactionsRes = await api.getStockTransactions(null, 200);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setEditSubmitting(false);
    }
  };

  // Get options for edit form
  const getEditFromOptions = () => {
    if (editingTransaction?.from_type === 'vendor') return vendors;
    if (editingTransaction?.from_type === 'customer') return customers;
    return offices;
  };

  const getEditToOptions = () => {
    if (editingTransaction?.to_type === 'customer') return customers;
    return offices;
  };

  // Export transactions to CSV
  const exportTransactions = () => {
    if (filteredTransactions.length === 0) {
      toast.error('No transactions to export');
      return;
    }

    const headers = ['Date', 'Type', 'From', 'To', 'SKU', 'Product Name', 'Quantity', 'Reference', 'Remarks'];
    
    const rows = [];
    filteredTransactions.forEach(txn => {
      txn.items.forEach(item => {
        rows.push([
          new Date(txn.transaction_date).toLocaleDateString(),
          txn.transaction_type,
          txn.from_name,
          txn.to_name,
          item.product_sku || '',
          item.product_name || '',
          item.quantity,
          txn.reference_number || '',
          txn.remarks || ''
        ]);
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => 
        typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
          ? `"${cell.replace(/"/g, '""')}"` 
          : cell
      ).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    let filename = 'transactions';
    if (filterStartDate || filterEndDate) {
      filename += `_${filterStartDate || 'start'}_to_${filterEndDate || 'end'}`;
    }
    filename += `_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${filteredTransactions.length} transactions`);
  };

  const clearDateFilter = () => {
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase': return <Truck className="text-green-600" size={18} />;
      case 'sales': return <ShoppingCart className="text-blue-600" size={18} />;
      case 'transfer': return <ArrowRightLeft className="text-purple-600" size={18} />;
      case 'return': return <RotateCcw className="text-orange-600" size={18} />;
      case 'scrap': return <Trash className="text-red-600" size={18} />;
      case 'existing_stock': return <PackagePlus className="text-cyan-600" size={18} />;
      default: return <Package size={18} />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'purchase': return 'bg-green-50 border-green-200';
      case 'sales': return 'bg-blue-50 border-blue-200';
      case 'transfer': return 'bg-purple-50 border-purple-200';
      case 'return': return 'bg-orange-50 border-orange-200';
      case 'scrap': return 'bg-red-50 border-red-200';
      case 'existing_stock': return 'bg-cyan-50 border-cyan-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="stock-inward-outward" className="max-w-[1600px] mx-auto space-y-8">
      <div>
        <h1 className="text-4xl font-bold font-headings uppercase tracking-tight text-slate-900 mb-2">
          Inward / Outward
        </h1>
        <p className="text-slate-600 font-body">Create stock transactions for purchases, sales, and transfers.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Transaction Form */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-semibold font-headings text-slate-900 mb-6 flex items-center gap-2">
            <ArrowRightLeft size={24} />
            New Transaction
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Transaction Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Transaction Type</label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {[
                  { value: 'purchase', label: 'Purchase', icon: Truck, color: 'green' },
                  { value: 'sales', label: 'Sales', icon: ShoppingCart, color: 'blue' },
                  { value: 'transfer', label: 'Transfer', icon: ArrowRightLeft, color: 'purple' },
                  { value: 'return', label: 'Return', icon: RotateCcw, color: 'orange' },
                  { value: 'scrap', label: 'Scrap', icon: Trash, color: 'red' },
                  { value: 'existing_stock', label: 'Existing Stock', icon: PackagePlus, color: 'cyan' }
                ].map(type => (
                  <button
                    key={type.value}
                    type="button"
                    data-testid={`txn-type-${type.value}`}
                    onClick={() => setTransactionType(type.value)}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      transactionType === type.value
                        ? `border-${type.color}-500 bg-${type.color}-50 text-${type.color}-700`
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    style={{
                      borderColor: transactionType === type.value ? 
                        (type.color === 'green' ? '#22c55e' : type.color === 'blue' ? '#3b82f6' : type.color === 'purple' ? '#a855f7' : type.color === 'orange' ? '#f97316' : type.color === 'red' ? '#ef4444' : '#06b6d4') : undefined,
                      backgroundColor: transactionType === type.value ?
                        (type.color === 'green' ? '#f0fdf4' : type.color === 'blue' ? '#eff6ff' : type.color === 'purple' ? '#faf5ff' : type.color === 'orange' ? '#fff7ed' : type.color === 'red' ? '#fef2f2' : '#ecfeff') : undefined
                    }}
                  >
                    <type.icon size={24} />
                    <span className="text-sm font-medium text-center">{type.label}</span>
                  </button>
                ))}
              </div>
              {transactionType === 'existing_stock' && (
                <p className="mt-2 text-xs text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-md px-3 py-2">
                  Use this only for first-time entry of existing inventory at a warehouse. It directly adds the quantity without a source/vendor.
                </p>
              )}
            </div>

            {/* From / To */}
            <div className={`grid ${(transactionType === 'scrap' || transactionType === 'existing_stock') ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
              {transactionType !== 'existing_stock' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    From ({fromType === 'vendor' ? 'Vendor' : fromType === 'customer' ? 'Customer' : 'Warehouse'})
                  </label>
                  <select
                    value={fromId}
                    onChange={(e) => setFromId(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    required
                  >
                    <option value="">Select {fromType}</option>
                    {getFromOptions().map(option => (
                      <option key={option.id} value={option.id}>{option.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {transactionType !== 'scrap' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    To ({toType === 'customer' ? 'Customer' : 'Warehouse'})
                  </label>
                  <select
                    value={toId}
                    onChange={(e) => setToId(e.target.value)}
                    data-testid="existing-stock-to-warehouse"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    required
                  >
                    <option value="">Select {toType}</option>
                    {getToOptions().map(option => (
                      <option key={option.id} value={option.id}>{option.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {transactionType === 'scrap' && (
                <div className="hidden">
                  {/* Hidden for scrap - no destination needed */}
                </div>
              )}
            </div>

            {/* Date & Reference */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Calendar size={16} className="inline mr-1" /> Date
                </label>
                <input
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <FileText size={16} className="inline mr-1" /> Reference / Invoice No.
                </label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="INV-001"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
            </div>

            {/* Products or Combos Toggle - hide for existing_stock */}
            {transactionType !== 'existing_stock' && (
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-slate-700">Item Mode</label>
                <div className="flex bg-slate-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setIsComboTransaction(false)}
                    className={`px-4 py-1.5 text-sm rounded-md transition-all ${
                      !isComboTransaction 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Individual Products
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsComboTransaction(true)}
                    className={`px-4 py-1.5 text-sm rounded-md transition-all ${
                      isComboTransaction 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Combos
                  </button>
                </div>
              </div>
            )}

            {/* Products (Individual Mode) */}
            {(!isComboTransaction || transactionType === 'existing_stock') && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">Products</label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                  >
                    <Plus size={16} /> Add Product
                  </button>
                </div>
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <ProductSelector
                        products={products}
                        value={item.product_id}
                        onChange={(productId) => updateItem(index, 'product_id', productId)}
                        placeholder="Search product..."
                      />
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        className="w-20 flex-shrink-0 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
                        required
                      />
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-2 flex-shrink-0 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Combos Mode */}
            {isComboTransaction && transactionType !== 'existing_stock' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">Combos</label>
                  <button
                    type="button"
                    onClick={addComboItem}
                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                  >
                    <Plus size={16} /> Add Combo
                  </button>
                </div>
                {combos.length === 0 ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                    No combos created yet. Go to <strong>Modify</strong> tab → <strong>Combos</strong> to create combos first.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {comboItems.map((item, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <select
                          value={item.combo_id}
                          onChange={(e) => updateComboItem(index, 'combo_id', e.target.value)}
                          className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                          required
                        >
                          <option value="">Select Combo</option>
                          {combos.map(combo => (
                            <option key={combo.id} value={combo.id}>
                              {combo.sku} - {combo.name} ({combo.items_count} items)
                            </option>
                          ))}
                        </select>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-500 text-sm">×</span>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateComboItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            placeholder="Qty"
                            className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm text-center"
                            required
                          />
                        </div>
                        {comboItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeComboItem(index)}
                            className="p-2 flex-shrink-0 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    {/* Combo Preview */}
                    {comboItems.some(ci => ci.combo_id) && (
                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-xs font-medium text-purple-700 mb-2">Items that will be affected:</p>
                        <div className="space-y-1 text-xs text-purple-600">
                          {comboItems.filter(ci => ci.combo_id).map((ci, idx) => {
                            const combo = combos.find(c => c.id === ci.combo_id);
                            if (!combo) return null;
                            return (
                              <div key={idx}>
                                <span className="font-medium">{combo.sku}</span> × {ci.quantity}:
                                <span className="ml-2">
                                  {combo.items?.map(i => `${i.product_sku} ×${i.quantity * ci.quantity}`).join(', ')}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Remarks (Optional)</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowRightLeft size={20} />
                  Create Transaction
                </>
              )}
            </button>
          </form>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold font-headings text-slate-900 flex items-center gap-2">
              <FileText size={24} />
              Transactions
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDateFilter(!showDateFilter)}
                className={`p-2 rounded-lg transition-colors ${showDateFilter ? 'bg-cyan-100 text-cyan-700' : 'hover:bg-slate-100 text-slate-600'}`}
                title="Filter by date"
              >
                <Filter size={18} />
              </button>
            </div>
          </div>

          {/* Date Filter & Export Section */}
          {showDateFilter && (
            <div className="mb-4 p-4 bg-gradient-to-r from-slate-50 to-cyan-50 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-3">Select Date Range for Export</p>
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">From Date</label>
                  <input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">To Date</label>
                  <input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 bg-white"
                  />
                </div>
                {(filterStartDate || filterEndDate) && (
                  <button
                    onClick={clearDateFilter}
                    className="px-3 py-2 text-sm text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg flex items-center gap-1 border border-slate-200"
                  >
                    <X size={14} /> Clear
                  </button>
                )}
                <button
                  onClick={exportTransactions}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  <Download size={16} />
                  Export CSV
                </button>
              </div>
              {(filterStartDate || filterEndDate) && (
                <p className="text-xs text-cyan-700 mt-3 font-medium">
                  📊 {filteredTransactions.length} transactions selected for export (out of {transactions.length} total)
                </p>
              )}
            </div>
          )}

          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ArrowRightLeft size={48} className="mx-auto mb-4 text-slate-300" />
              <p>{transactions.length === 0 ? 'No transactions yet' : 'No transactions match the filter'}</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredTransactions.map(txn => (
                <div
                  key={txn.id}
                  className={`p-4 rounded-lg border ${getTransactionColor(txn.transaction_type)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getTransactionIcon(txn.transaction_type)}
                      <span className="font-medium capitalize">
                        {txn.transaction_type === 'existing_stock' ? 'Existing Stock' : txn.transaction_type}
                      </span>
                      {isTransactionEditable(txn) && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Clock size={10} />
                          {getRemainingEditTime(txn)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">
                        {new Date(txn.transaction_date).toLocaleDateString()}
                      </span>
                      {isTransactionEditable(txn) && (
                        <button
                          onClick={() => handleEditTransaction(txn)}
                          className="p-1 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                          title="Edit transaction (available for 24 hours)"
                          data-testid={`edit-txn-${txn.id}`}
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteTransaction(txn.id)}
                        disabled={deleting === txn.id}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Delete transaction"
                      >
                        {deleting === txn.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-slate-600 mb-2">
                    <span className="font-medium">{txn.from_name}</span>
                    <span className="mx-2">→</span>
                    <span className="font-medium">{txn.to_name}</span>
                  </div>
                  {/* Show item names */}
                  <div className="space-y-1 mb-2">
                    {txn.items.map((item, idx) => (
                      <div key={idx} className="text-sm flex items-center justify-between bg-white/50 px-2 py-1 rounded">
                        <span className="text-slate-700">
                          <span className="font-mono text-xs text-slate-500">{item.product_sku}</span>
                          {' - '}
                          <span>{item.product_name}</span>
                        </span>
                        <span className="font-medium text-slate-900">{item.quantity} pcs</span>
                      </div>
                    ))}
                  </div>
                  {txn.reference_number && (
                    <div className="text-xs text-slate-500">
                      Ref: {txn.reference_number}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Transaction Modal */}
      {showEditModal && editingTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-slate-50">
              <div>
                <h3 className="text-lg font-semibold">Edit Transaction</h3>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <Clock size={14} />
                  {getRemainingEditTime(editingTransaction)} to edit
                </p>
              </div>
              <button 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTransaction(null);
                }}
                className="p-1 hover:bg-slate-200 rounded"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)] space-y-4">
              {/* Transaction Type (read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Transaction Type</label>
                <div className="px-3 py-2 bg-slate-100 rounded-lg capitalize font-medium">
                  {editingTransaction.transaction_type}
                </div>
              </div>

              {/* From / To */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    From ({editingTransaction.from_type})
                  </label>
                  <select
                    value={editingTransaction.editFromId}
                    onChange={(e) => updateEditField('editFromId', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">Select {editingTransaction.from_type}</option>
                    {getEditFromOptions().map(option => (
                      <option key={option.id} value={option.id}>{option.name}</option>
                    ))}
                  </select>
                </div>
                {editingTransaction.transaction_type !== 'scrap' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      To ({editingTransaction.to_type})
                    </label>
                    <select
                      value={editingTransaction.editToId}
                      onChange={(e) => updateEditField('editToId', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">Select {editingTransaction.to_type}</option>
                      {getEditToOptions().map(option => (
                        <option key={option.id} value={option.id}>{option.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Date & Reference */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={editingTransaction.editTransactionDate}
                    onChange={(e) => updateEditField('editTransactionDate', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reference Number</label>
                  <input
                    type="text"
                    value={editingTransaction.editReferenceNumber}
                    onChange={(e) => updateEditField('editReferenceNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    placeholder="Invoice/PO number"
                  />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">Items</label>
                  <button
                    type="button"
                    onClick={addEditItem}
                    className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                  >
                    <Plus size={14} /> Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {editingTransaction.editItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <select
                          value={item.product_id}
                          onChange={(e) => updateEditItem(index, 'product_id', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Select Product</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateEditItem(index, 'quantity', parseInt(e.target.value) || 0)}
                        className="w-20 px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-cyan-500 text-center"
                        min="1"
                        placeholder="Qty"
                      />
                      {editingTransaction.editItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEditItem(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Remarks</label>
                <textarea
                  value={editingTransaction.editRemarks}
                  onChange={(e) => updateEditField('editRemarks', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  rows={2}
                  placeholder="Optional notes..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-slate-50">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTransaction(null);
                }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitEdit}
                disabled={editSubmitting}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {editSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockInwardOutward;
